import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const EditAbsen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [absensiList, setAbsensiList] = useState([]);
  const [filteredAbsensi, setFilteredAbsensi] = useState([]);
  const [error, setError] = useState("");

  // Filter states
  const [filterTanggal, setFilterTanggal] = useState("");
  const [filterKelas, setFilterKelas] = useState("");
  const [filterRombel, setFilterRombel] = useState("");
  const [tahun, setTahun] = useState("");
  const [semester, setSemester] = useState("");

  useEffect(() => {
    const fetchAllAbsensi = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Unauthorized: No token found");

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}api/absensi/getall`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setAbsensiList(response.data);
      } catch (err) {
        setError(err.response?.data?.msg || "Failed to fetch absensi data");
      }
    };

    fetchAllAbsensi();
  }, []);

  const handleFilter = () => {
    let filtered = absensiList;

    if (filterTanggal) {
      filtered = filtered.filter(
        (absensi) =>
          new Date(absensi.tanggal).toLocaleDateString("en-CA") ===
          filterTanggal,
      );
    }
    if (filterKelas) {
      filtered = filtered.filter((absensi) => absensi.kelas === filterKelas);
    }
    if (filterRombel) {
      filtered = filtered.filter((absensi) => absensi.rombel === filterRombel);
    }

    const siswaData = filtered.flatMap((absensi) =>
      (absensi.siswa || []).map((siswa) => ({
        ...siswa,
        absensiId: absensi._id,
        kelas: absensi.kelas,
        rombel: absensi.rombel,
        tanggal: absensi.tanggal,
        semester: absensi.semester,
        tahun: absensi.tahun,
      })),
    );

    setFilteredAbsensi(siswaData);
    if (filtered.length > 0) {
      setTahun(filtered[0].tahun);
      setSemester(filtered[0].semester);
    } else {
      setTahun("");
      setSemester("");
    }
  };

  const handleChange = (absensiId, siswaId, field, value) => {
    setFilteredAbsensi((prev) =>
      prev.map((siswa) =>
        siswa.absensiId === absensiId && siswa._id === siswaId
          ? { ...siswa, [field]: value }
          : siswa,
      ),
    );
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Unauthorized: No token found");

      const groupedByAbsensi = filteredAbsensi.reduce((acc, siswa) => {
        const { absensiId, ...siswaData } = siswa;
        acc[absensiId] = acc[absensiId] || [];
        acc[absensiId].push(siswaData);
        return acc;
      }, {});

      for (const absensiId in groupedByAbsensi) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}api/absensi/${absensiId}`,
          {
            siswa: groupedByAbsensi[absensiId].map((siswa) => ({
              ...siswa,
              sakit: Number(siswa.sakit) || 0,
              izin: Number(siswa.izin) || 0,
              tanpa_keterangan: Number(siswa.tanpa_keterangan) || 0,
            })),
            semester,
            tahun,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      alert("Data successfully saved!");
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to update absensi data");
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Unauthorized: No token found");

      const absensiIds = [
        ...new Set(filteredAbsensi.map((siswa) => siswa.absensiId)),
      ];

      for (const absensiId of absensiIds) {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}api/absensi/${absensiId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }

      alert("Selected records successfully deleted!");
      setFilteredAbsensi([]);
      handleFilter(); // Refresh filtered data
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to delete absensi data");
    }
  };

  return (
    <div className="min-h-screen p-4 space-y-6 bg-gray-100">
      {/* ===== HEADER ===== */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800">Edit Absensi</h1>
        <p className="text-sm text-gray-500">Perbarui data absensi siswa</p>
      </div>

      {/* ===== FILTER CARD ===== */}
      <div className="bg-white p-4 rounded-xl shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium">Tanggal</label>
            <input
              type="date"
              value={filterTanggal}
              onChange={(e) => setFilterTanggal(e.target.value)}
              className="input w-full"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Kelas</label>
            <select
              value={filterKelas}
              onChange={(e) => setFilterKelas(e.target.value)}
              className="input w-full"
            >
              <option value="">Pilih</option>
              {[1, 2, 3, 4, 5, 6].map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">Rombel</label>
            <select
              value={filterRombel}
              onChange={(e) => setFilterRombel(e.target.value)}
              className="input w-full"
            >
              <option value="">Pilih</option>
              {["A", "B", "C", "D"].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleFilter}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              🔍 Filter
            </button>
          </div>
        </div>
      </div>

      {/* ===== TAHUN & SEMESTER ===== */}
      {filteredAbsensi.length > 0 && (
        <div className="bg-white p-4 rounded-xl shadow grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Tahun</label>
            <input
              type="text"
              value={tahun}
              onChange={(e) => setTahun(e.target.value)}
              className="input w-full"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Semester</label>
            <select
              value={semester}
              onChange={(e) => setSemester(e.target.value)}
              className="input w-full"
            >
              <option value="">Pilih</option>
              <option value="1">Semester 1</option>
              <option value="2">Semester 2</option>
            </select>
          </div>
        </div>
      )}

      {/* ===== TABLE ===== */}
      {filteredAbsensi.length > 0 && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-auto max-h-[420px]">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-100">
                <tr>
                  <th className="th">No</th>
                  <th className="th">Nama Siswa</th>
                  <th className="th">Sakit</th>
                  <th className="th">Izin</th>
                  <th className="th">Tanpa Ket.</th>
                </tr>
              </thead>
              <tbody>
                {filteredAbsensi.map((s, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="td text-center">{i + 1}</td>
                    <td className="td">{s.namaSiswa}</td>
                    {["sakit", "izin", "tanpa_keterangan"].map((key) => (
                      <td key={key} className="td">
                        <input
                          type="number"
                          min={0}
                          max={1}
                          step={1}
                          value={s[key]}
                          onChange={(e) => {
                            const val =
                              e.target.value === ""
                                ? 0
                                : Number(e.target.value);
                            if (val === 0 || val === 1) {
                              handleChange(s.absensiId, s._id, key, val);
                            }
                          }}
                          className="w-20 text-center border rounded px-2 py-1"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== ACTIONS ===== */}
      {filteredAbsensi.length > 0 && (
        <div className="flex flex-wrap justify-center gap-4">
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            💾 Simpan Perubahan
          </button>
          <button
            onClick={handleDelete}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            🗑 Hapus Data
          </button>
        </div>
      )}

      {/* ===== BACK ===== */}
      <div className="flex justify-center">
        <button
          onClick={() => navigate(`/absensi/${id}`)}
          className="text-blue-600 hover:underline"
        >
          ⬅ Kembali
        </button>
      </div>

      {/* ERROR */}
      {error && <p className="text-center text-red-500">{error}</p>}
    </div>
  );
};

export default EditAbsen;
